/**
 * Card Games
 * Solitaire and Blackjack
 */

import React, { useState } from 'react';
import { useLocalStorage } from './hooks/useLocalStorage';
import useGameAI from './hooks/useGameAI';
import GameLayout from './common/GameLayout';
import ScoreDisplay from './common/ScoreDisplay';
import { GAME_CONFIG } from './config/gameConfig';

const CardGames = ({ onHome }) => {
  const [gameState, setGameState] = useState(GAME_CONFIG.states.MENU);
  const [gameType, setGameType] = useState('solitaire');
  const [score, setScore] = useLocalStorage('cardgames_score', 0);
  const [highScore, setHighScore] = useLocalStorage('cardgames_highScore', 0);
  const { getAIMove } = useGameAI();

  // Blackjack state
  const [playerHand, setPlayerHand] = useState([]);
  const [dealerHand, setDealerHand] = useState([]);
  const [bet, setBet] = useState(10);
  const [chips, setChips] = useState(100);
  const [blackjackState, setBlackjackState] = useState('betting'); // betting, playing, done
  const [message, setMessage] = useState('');
  const [difficulty, setDifficulty] = useState('easy'); // 'easy', 'medium', 'hard'
  const [gamePhase, setGamePhase] = useState('menu'); // menu, difficulty, solitaire, blackjack

  // Solitaire state
  const [tableau, setTableau] = useState([]);
  const [foundation, setFoundation] = useState({});
  const [stock, setStock] = useState([]);
  const [waste, setWaste] = useState([]);

  const createDeck = () => {
    const suits = ['♠', '♥', '♦', '♣'];
    const ranks = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
    const deck = [];
    for (let suit of suits) {
      for (let rank of ranks) {
        deck.push({ suit, rank });
      }
    }
    return deck.sort(() => Math.random() - 0.5);
  };

  const getCardValue = (card) => {
    if (card.rank === 'A') return 11;
    if (['J', 'Q', 'K'].includes(card.rank)) return 10;
    return parseInt(card.rank);
  };

  const calculateHandValue = (hand) => {
    let value = hand.reduce((sum, card) => sum + getCardValue(card), 0);
    let aces = hand.filter((c) => c.rank === 'A').length;
    while (value > 21 && aces > 0) {
      value -= 10;
      aces--;
    }
    return value;
  };

  const startSolitaire = () => {
    const deck = createDeck();
    const newTableau = [];
    let deckIndex = 0;

    // Create tableau piles
    for (let i = 0; i < 7; i++) {
      newTableau[i] = [];
      for (let j = 0; j <= i; j++) {
        newTableau[i].push(deck[deckIndex++]);
      }
    }

    setTableau(newTableau);
    setStock(deck.slice(deckIndex));
    setWaste([]);
    setFoundation({});
    setScore(0);
    setGameType('solitaire');
    setGamePhase('playing');
    setGameState(GAME_CONFIG.states.PLAYING);
  };

  const startBlackjack = () => {
    if (chips <= 0) {
      setMessage('Game Over! No chips left.');
      return;
    }
    const deck = createDeck();
    const pHand = [deck[0], deck[1]];
    const dHand = [deck[2], deck[3]];
    setPlayerHand(pHand);
    setDealerHand(dHand);
    setBlackjackState('playing');
    setBet(10);
    setMessage('Hit or Stand?');
    setGameType('blackjack');
    setGamePhase('playing');
    setGameState(GAME_CONFIG.states.PLAYING);
  };

  const hitBlackjack = () => {
    const deck = createDeck();
    const newHand = [...playerHand, deck[0]];
    setPlayerHand(newHand);

    const playerValue = calculateHandValue(newHand);
    if (playerValue > 21) {
      endBlackjackRound(playerValue, calculateHandValue(dealerHand), true);
    }
  };

  const standBlackjack = () => {
    let dealerHand_ = [...dealerHand];
    const deck = createDeck();
    let dealerIndex = 4;

    // Dealer strategy based on difficulty
    if (difficulty === 'easy') {
      // Easy: always hit on < 17
      while (calculateHandValue(dealerHand_) < 17) {
        dealerHand_.push(deck[dealerIndex++]);
      }
    } else if (difficulty === 'medium') {
      // Medium: hit on < 17, but sometimes stand on 17
      while (calculateHandValue(dealerHand_) < 16) {
        dealerHand_.push(deck[dealerIndex++]);
      }
      // 40% chance to hit on 17
      if (calculateHandValue(dealerHand_) === 17 && Math.random() < 0.4) {
        dealerHand_.push(deck[dealerIndex++]);
      }
    } else if (difficulty === 'hard') {
      // Hard: advanced strategy
      const playerValue = calculateHandValue(playerHand);
      const dealerValue = calculateHandValue(dealerHand_);

      // Simulate dealer with basic strategy
      while (calculateHandValue(dealerHand_) < 17) {
        dealerHand_.push(deck[dealerIndex++]);
      }

      // If dealer is soft 17 and player is strong, hit
      if (dealerValue === 17 && dealerHand_.some(c => c.rank === 'A') && playerValue > 17) {
        dealerHand_.push(deck[dealerIndex++]);
      }
    }

    const playerValue = calculateHandValue(playerHand);
    const dealerValue = calculateHandValue(dealerHand_);

    endBlackjackRound(playerValue, dealerValue, false);
  };

  const endBlackjackRound = (playerValue, dealerValue, playerBusted) => {
    let result = '';
    let newChips = chips;

    if (playerBusted) {
      result = 'You Busted! Dealer wins.';
      newChips -= bet;
    } else if (dealerValue > 21) {
      result = 'Dealer Busted! You win!';
      newChips += bet;
      setScore((prev) => prev + 50);
    } else if (playerValue > dealerValue) {
      result = 'You win!';
      newChips += bet;
      setScore((prev) => prev + 50);
    } else if (playerValue < dealerValue) {
      result = 'Dealer wins!';
      newChips -= bet;
    } else {
      result = "It's a tie!";
    }

    setChips(newChips);
    setMessage(result);
    setBlackjackState('done');

    if (newChips <= 0) {
      setTimeout(() => {
        alert('Game Over! No chips left.');
        setGameState(GAME_CONFIG.states.MENU);
      }, 1500);
    }
  };

  const resetGame = () => {
    if (gameType === 'solitaire' && Object.keys(foundation).length > 0) {
      setScore((prev) => prev + 100);
    }
    setGamePhase('menu');
    setGameState(GAME_CONFIG.states.MENU);
  };

  const cardDisplay = (card) => {
    if (!card) return '';
    return `${card.rank}${card.suit}`;
  };

  return (
    <GameLayout
      title="Card Games 🃏"
      score={score}
      highScore={highScore}
      gameState={gameState}
      onRestart={resetGame}
      onHome={onHome}
    >
      <div style={styles.gameArea}>
        {/* Game Type Selection */}
        {gameState === GAME_CONFIG.states.MENU && gamePhase === 'menu' && (
          <div style={styles.menuContainer}>
            <h2 style={styles.menuTitle}>Card Games</h2>
            <p style={styles.menuDescription}>
              Choose a card game to play
            </p>
            <div style={styles.gameOptions}>
              <button
                onClick={startSolitaire}
                className="btn btn-primary btn-lg"
                style={{ marginRight: '1rem' }}
              >
                🎴 Solitaire
              </button>
              <button
                onClick={() => setGamePhase('difficulty')}
                className="btn btn-success btn-lg"
              >
                ♠️ Blackjack
              </button>
            </div>
          </div>
        )}

        {/* Difficulty Selection for Blackjack */}
        {gameState === GAME_CONFIG.states.MENU && gamePhase === 'difficulty' && (
          <div style={styles.menuContainer}>
            <h2 style={styles.menuTitle}>Select Difficulty</h2>
            <p style={styles.menuDescription}>
              Choose your difficulty level
            </p>
            <div style={styles.difficultyButtonsContainer}>
              <button
                onClick={() => {
                  setDifficulty('easy');
                  startBlackjack();
                }}
                className="btn btn-primary"
              >
                🟢 Easy
              </button>
              <button
                onClick={() => {
                  setDifficulty('medium');
                  startBlackjack();
                }}
                className="btn btn-warning"
              >
                🟡 Medium
              </button>
              <button
                onClick={() => {
                  setDifficulty('hard');
                  startBlackjack();
                }}
                className="btn btn-danger"
              >
                🔴 Hard
              </button>
            </div>
          </div>
        )}

        {gameState === GAME_CONFIG.states.PLAYING && gameType === 'solitaire' && (
          <div style={styles.solitaireArea}>
            <h3 style={{ color: GAME_CONFIG.colors.light }}>Klondike Solitaire</h3>
            <div style={styles.solitaireBoard}>
              <div style={styles.stockArea}>
                <div
                  style={{
                    ...styles.card,
                    backgroundColor: stock.length > 0 ? GAME_CONFIG.colors.primary : '#333',
                  }}
                >
                  {stock.length > 0 ? `${stock.length} cards` : 'Stock'}
                </div>
                <div style={styles.card}>Waste</div>
              </div>
              <div style={styles.foundationArea}>
                {['♠', '♥', '♦', '♣'].map((suit) => (
                  <div
                    key={suit}
                    style={{
                      ...styles.card,
                      backgroundColor: GAME_CONFIG.colors.accent2,
                      color: '#000',
                    }}
                  >
                    {suit}
                  </div>
                ))}
              </div>
            </div>
            <div style={styles.message}>Click stock to reveal cards. Drag cards to foundation or tableau.</div>
          </div>
        )}

        {gameState === GAME_CONFIG.states.PLAYING && gameType === 'blackjack' && (
          <div style={styles.blackjackArea}>
            <h3 style={{ color: GAME_CONFIG.colors.light }}>Blackjack</h3>
            <div style={styles.blackjackInfo}>
              <div>Chips: {chips}</div>
              <div>Bet: {bet}</div>
            </div>

            <div style={styles.blackjackHands}>
              <div style={styles.hand}>
                <h4>Dealer</h4>
                <div style={styles.cardRow}>
                  {dealerHand.map((card, idx) => (
                    <div
                      key={idx}
                      style={{
                        ...styles.card,
                        backgroundColor: GAME_CONFIG.colors.accent,
                      }}
                    >
                      {cardDisplay(card)}
                    </div>
                  ))}
                </div>
                {blackjackState === 'done' && (
                  <div style={{ color: GAME_CONFIG.colors.accent2 }}>
                    Value: {calculateHandValue(dealerHand)}
                  </div>
                )}
              </div>

              <div style={styles.hand}>
                <h4>You</h4>
                <div style={styles.cardRow}>
                  {playerHand.map((card, idx) => (
                    <div
                      key={idx}
                      style={{
                        ...styles.card,
                        backgroundColor: GAME_CONFIG.colors.success,
                      }}
                    >
                      {cardDisplay(card)}
                    </div>
                  ))}
                </div>
                <div style={{ color: GAME_CONFIG.colors.accent2 }}>
                  Value: {calculateHandValue(playerHand)}
                </div>
              </div>
            </div>

            <div style={styles.message}>{message}</div>

            <div style={styles.blackjackActions}>
              {blackjackState === 'playing' && (
                <>
                  <button onClick={hitBlackjack} className="btn btn-primary">
                    Hit
                  </button>
                  <button onClick={standBlackjack} className="btn btn-warning">
                    Stand
                  </button>
                </>
              )}
              {blackjackState === 'done' && (
                <button
                  onClick={startBlackjack}
                  className="btn btn-success"
                >
                  Play Again
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </GameLayout>
  );
};

const styles = {
  gameArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1.5rem',
    width: '100%',
  },
  menuContainer: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '3rem 2rem',
    textAlign: 'center',
  },
  menuTitle: {
    color: GAME_CONFIG.colors.accent,
    fontSize: '2rem',
    marginBottom: '1rem',
    fontWeight: 'bold',
  },
  menuDescription: {
    color: GAME_CONFIG.colors.light,
    marginBottom: '2rem',
  },
  gameOptions: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'center',
  },
  difficultyButtonsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  solitaireArea: {
    color: GAME_CONFIG.colors.light,
    padding: '2rem',
    backgroundColor: `rgba(0, 0, 0, 0.3)`,
    borderRadius: GAME_CONFIG.radius.lg,
    minWidth: '500px',
  },
  solitaireBoard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2rem',
    marginTop: '1.5rem',
  },
  stockArea: {
    display: 'flex',
    gap: '1rem',
  },
  foundationArea: {
    display: 'flex',
    gap: '1rem',
  },
  blackjackArea: {
    color: GAME_CONFIG.colors.light,
    padding: '2rem',
    backgroundColor: `rgba(0, 0, 0, 0.3)`,
    borderRadius: GAME_CONFIG.radius.lg,
    minWidth: '500px',
  },
  blackjackInfo: {
    display: 'flex',
    gap: '2rem',
    marginBottom: '1.5rem',
    padding: '1rem',
    backgroundColor: `rgba(0, 212, 255, 0.1)`,
    borderRadius: GAME_CONFIG.radius.md,
  },
  blackjackHands: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2rem',
    marginBottom: '1.5rem',
  },
  hand: {
    padding: '1rem',
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    borderRadius: GAME_CONFIG.radius.md,
    border: `1px solid ${GAME_CONFIG.colors.primary}`,
  },
  cardRow: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '0.5rem',
  },
  card: {
    width: '60px',
    height: '80px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    border: '1px solid #fff',
    fontWeight: 'bold',
    fontSize: '0.9rem',
    color: '#fff',
  },
  message: {
    padding: '1rem',
    backgroundColor: `rgba(102, 126, 234, 0.2)`,
    borderRadius: GAME_CONFIG.radius.md,
    marginBottom: '1rem',
    textAlign: 'center',
    color: GAME_CONFIG.colors.accent,
  },
  blackjackActions: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'center',
  },
};

export default CardGames;
